import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, History, Star, Truck } from 'lucide-react';
import api from '@/lib/api';

/**
 * Dua field terkait (Nama Sopir + Plat Kendaraan) dengan autocomplete dari histori.
 * Ambil `/settings/drivers` sekali per mount, tampilkan sebagai popover.
 * Klik salah satu entry → isi kedua field sekaligus.
 *
 * Props:
 *   - driver, setDriver: state nama sopir
 *   - plate, setPlate: state plat
 *   - required: bool (untuk asterisk)
 *   - onPick(entry): optional callback saat pilih dari histori
 *   - testIdPrefix: prefix untuk data-testid
 */
export default function DriverAutocomplete({
  driver,
  setDriver,
  plate,
  setPlate,
  required = false,
  onPick,
  testIdPrefix = 'driver-autocomplete',
}) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/settings/drivers');
        if (mounted) setDrivers(data.drivers || []);
      } catch (_e) {
        if (mounted) setDrivers([]);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const topSuggestions = useMemo(() => drivers.slice(0, 4), [drivers]);

  const pick = (entry) => {
    setDriver(entry.driver_name || '');
    setPlate((entry.vehicle_plate || '').toUpperCase());
    setOpen(false);
    if (onPick) onPick(entry);
  };

  const req = required ? <span className="text-rose-600">*</span> : null;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Nama Sopir {req}</Label>
          <Input
            value={driver}
            onChange={(e) => setDriver(e.target.value)}
            placeholder="mis. Budi"
            data-testid={`${testIdPrefix}-driver`}
          />
        </div>
        <div>
          <Label className="text-xs">Plat Kendaraan {req}</Label>
          <Input
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            placeholder="B 1234 XYZ"
            data-testid={`${testIdPrefix}-plate`}
          />
        </div>
      </div>
      {/* Histori & Popover */}
      {drivers.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-muted-foreground shrink-0">Sopir sering:</span>
          {topSuggestions.map((d, idx) => (
            <Badge
              key={`${d.driver_name}-${d.vehicle_plate}-${idx}`}
              variant="outline"
              className="cursor-pointer hover:bg-primary/10 hover:border-primary text-[10px] py-0.5 px-1.5 h-5"
              onClick={() => pick(d)}
              data-testid={`${testIdPrefix}-quick-${idx}`}
            >
              {d.source === 'master' ? <Star className="h-2.5 w-2.5 mr-0.5 fill-amber-400 text-amber-500" /> : null}
              <span className="truncate max-w-[100px]">{d.driver_name}</span>
              <span className="text-muted-foreground mx-1">·</span>
              <span className="font-mono">{d.vehicle_plate}</span>
            </Badge>
          ))}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 text-[10px] px-1.5 hover:bg-accent"
                data-testid={`${testIdPrefix}-more-btn`}
              >
                <History className="h-2.5 w-2.5 mr-0.5" />
                Semua ({drivers.length})
                <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-[320px]" data-testid={`${testIdPrefix}-popover`}>
              <Command>
                <CommandInput placeholder="Cari nama sopir atau plat..." />
                <CommandList>
                  <CommandEmpty>Tidak ada yang cocok</CommandEmpty>
                  {drivers.some((d) => d.source === 'master') && (
                    <CommandGroup heading="Master (dikelola Owner)">
                      {drivers.filter((d) => d.source === 'master').map((d, i) => (
                        <CommandItem
                          key={`m-${i}`}
                          value={`${d.driver_name} ${d.vehicle_plate}`}
                          onSelect={() => pick(d)}
                          data-testid={`${testIdPrefix}-item-master-${i}`}
                        >
                          <Star className="h-3 w-3 mr-2 fill-amber-400 text-amber-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{d.driver_name}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{d.vehicle_plate}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {drivers.some((d) => d.source === 'history') && (
                    <CommandGroup heading="Dari histori pengiriman">
                      {drivers.filter((d) => d.source === 'history').map((d, i) => (
                        <CommandItem
                          key={`h-${i}`}
                          value={`${d.driver_name} ${d.vehicle_plate}`}
                          onSelect={() => pick(d)}
                          data-testid={`${testIdPrefix}-item-history-${i}`}
                        >
                          <Truck className="h-3 w-3 mr-2 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{d.driver_name}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">
                              {d.vehicle_plate} · {d.count}x kirim
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      {loading && <div className="text-[10px] text-muted-foreground">Memuat histori sopir...</div>}
    </div>
  );
}
